// 介錯の戦いの紫 (致命の光を除く) を常夜燈の青へ写す (2026-11-01・作者の原文は src/exec_look_1101.ts)。
//   写し方は src/purple_swap.ts と同じ (明るさはそのまま・色味だけ替える)。行き先の3色 = 常夜燈の光の粒 (src/lamp_mote.ts) の deep・mid・core。
//   替える所: 砲への組み変わりの光と組み変わりの絵 (morph_*・rise*)・砲の溜めの背光・直射と速射の溜めと弾・砲の空撃ちの残像・発勁 (輪・芯・溜め)。
//   替えない所: ボスの致命の紫 (弾ける致命の合図のオーラ・致命の炎と着弾・固有の致命)・致命を弾いた時の光。
//   前の紫 = DEV ?execTint=purple。
import { execLampTint } from './exec_look_1101'
import { LAMP_MOTE } from './lamp_mote'
import { sw, swapCssRamp, swapImage, swapImageRamp, type PurpleSwapRole, type SwapRamp, type SwapTone } from './purple_swap'

function tone(rgb: readonly [number, number, number]): SwapTone {
  const [r, g, b] = rgb.map(v => v / 255)
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b)
  const l = (mx + mn) / 2, d = mx - mn
  const s = d === 0 ? 0 : l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
  const h = d === 0 ? 0 : mx === r ? ((g - b) / d + (g < b ? 6 : 0)) * 60 : mx === g ? ((b - r) / d + 2) * 60 : ((r - g) / d + 4) * 60
  return { h, s }
}
// 紫は同じ明るさの青より暗く見えにくいので、輝度は 1.15 倍へ寄せる (常夜燈は冷たく明るい光)。
export const LAMP_RAMP: SwapRamp = { label: '常夜燈', dark: tone(LAMP_MOTE.deep), mid: tone(LAMP_MOTE.mid), light: tone(LAMP_MOTE.core), yMul: 1.15 }

const cache = new Map<string, string>()
/** 介錯の紫の色を写す。既定 = 常夜燈の青。?execTint=purple の時は前の通り (開発版の ?purpleSwap も効く)。 */
export function et(role: PurpleSwapRole, css: string): string {
  if (!execLampTint()) return sw(role, css)
  const hit = cache.get(css)
  if (hit !== undefined) return hit
  const out = swapCssRamp(LAMP_RAMP, css)
  cache.set(css, out)
  return out
}
/** 介錯の紫の絵を写す。 */
export function etImg<T extends CanvasImageSource>(img: T): T | HTMLCanvasElement {
  return execLampTint() ? swapImageRamp('lamp', LAMP_RAMP, img) : swapImage('parry', img)
}

// 体の絵に描き込まれた紫 (組み変わりの光・起き上がりの残り火)。写すのは介錯のこの絵だけ。
const BODY_KEYS = new Set(['morph_a', 'morph_b', 'morph_c', 'rise1', 'rise2'])
const warmed = new WeakSet<object>()
/** 介錯の体の絵を描く前に通す。写す絵は青へ写し、他はそのまま返す。 */
export function execBodyImg(bossId: string, key: string, img: HTMLImageElement): CanvasImageSource {
  if (bossId !== 'the-executioner' || !execLampTint() || !BODY_KEYS.has(key) || !img.complete || !img.naturalWidth) return img
  warmed.add(img)
  return swapImageRamp('lamp', LAMP_RAMP, img)
}
/** 初めて出る時に引っかからないよう、読み終えた写す絵を1コマに1枚ずつ先に写しておく。 */
export function warmExecBodyImgs(bossId: string, map: ReadonlyMap<string, HTMLImageElement>): void {
  if (bossId !== 'the-executioner' || !execLampTint()) return
  for (const key of BODY_KEYS) {
    const img = map.get(key)
    if (!img || warmed.has(img) || !img.complete || !img.naturalWidth) continue
    execBodyImg(bossId, key, img)
    return
  }
}
